import { useLocation, useNavigate } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import { useTripRateContext } from './TripRateContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { SessionContext } from '../Contexts/SessionContext';
import { APIContext, DialogAction } from '../Contexts/APIContext';
import { TelemetryContext } from '../Contexts/TelemetryContext';
import { getFilteredSurveys } from '../TripRateNew/TriprateFilters';
import SummaryDialog from '../TripRateNew/Summary/SummaryDialog';
import SiteMapDialog from '../Site/SiteMap/SiteMap';
import { SessionState } from '../Contexts/SessionState';
import { getValidActiveSurveyRefs, surveyRefsMatch } from './TripRateSurveyRefs';

const steps = [
    { label: 'Initial Filters', path: '/triprate/landuse' },
    { label: 'Trip Rate Parameters', path: '/triprate/params' },
    { label: 'Site Characteristics', path: '/triprate/characteristics' },
    { label: 'Population Factors', path: '/triprate/population' },
    { label: 'Summary', path: '/triprate/summary' },
    { label: 'Results', path: '/triprate/results', altPath: '/triprate/rankorder' },
];

export default function RouteStepper() {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { reset, surveys, loading } = useTripRateContext();
    const { sessionState, updateSessionState } = useContext(SessionContext);
    const { openDialog } = useContext(APIContext);
    const { logTripRateCalculationRun } = useContext(TelemetryContext);

    const [mapOpen, setMapOpen] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);

    const isFilterRoute = location.pathname === '/triprate/filter';
    const searchParams = new URLSearchParams(location.search);
    const origin = searchParams.get('origin');


    const filteredSurveys = useMemo(
        () => {
            const filtered = getFilteredSurveys(surveys);
            if (location.pathname.includes("results") || location.pathname.includes("summary") || location.pathname.includes("rankorder")) {
                const activeRefs = getValidActiveSurveyRefs(
                    sessionState.selectedSitesAndSurveys,
                    filtered.map(survey => survey.surveyRef),
                );
                return filtered.filter(survey => activeRefs.some(activeRef => surveyRefsMatch(activeRef, survey.surveyRef)));
            }
            return filtered;
        }, [surveys, sessionState.primaryFilters, sessionState.secondaryFilters, sessionState.residentialUnitTypes, sessionState.landusePage, location.pathname, sessionState.selectedSitesAndSurveys]
    );

    useEffect(() => {

        if (location.pathname != "/triprate/summary" && location.pathname != "/triprate/results" && location.pathname != "/triprate/rankorder") {
            updateSessionState({ selectedSitesAndSurveys: [] })
        }

    }, [location.pathname])

    useEffect(() => {
        setMapOpen(origin === 'map');
    }, [origin]);


    const { manualCount, atcCount, reSurveyCount } = useMemo(() => {
        if (!filteredSurveys?.length) return { manualCount: 0, atcCount: 0, reSurveyCount: 0 };

        const filtered = getFilteredSurveys(surveys);
        const filteredRefs = filtered.map(m => m.reference);
        const manual = filteredSurveys.filter(s => !s.isATC).length;
        const atc = filteredSurveys.filter(s => s.isATC).length;
        let reSurvey = filteredSurveys.filter(s => filteredRefs.includes(s.resurveyId)).length;

        if (location.pathname.includes("results") || location.pathname.includes("summary") || location.pathname.includes("rankorder")) {
            reSurvey = filtered.length - filteredSurveys.length;
        }

        return { manualCount: manual, atcCount: atc, reSurveyCount: reSurvey };
    }, [filteredSurveys]);

    const currentStepIndex = Math.max(
        0,
        steps.findIndex(s => s.path === location.pathname || s.altPath === location.pathname)
    );

    const goToStep = (i: number) => {
        if (i < 0 || i >= steps.length) return;
        navigate(steps[i].path);
    };

    const appendFromResults = (toPath: string) => {
        const params = new URLSearchParams(location.search);
        params.set('fromResults', 'true');
        navigate({ pathname: toPath, search: params.toString() }, { replace: false });
    };

    const onBack = () => {
        if (isFilterRoute) {
            navigate('/triprate/landuse');
            return;
        }

        if (currentStepIndex <= 0) return;

        const prevPath = steps[currentStepIndex - 1].path;

        const isOnResults =
            location.pathname === '/triprate/results' ||
            location.pathname === '/triprate/rankorder';

        if (isOnResults) {
            appendFromResults(prevPath);
            return;
        }

        goToStep(currentStepIndex - 1);
    };

    const onNext = () => { if (currentStepIndex < steps.length - 1) goToStep(currentStepIndex + 1); };

    const handleCalculateTripRate = () => {
        logTripRateCalculationRun(sessionState);
        onNext();
    };

    const handleReset = async () => {
        openDialog(
            'Are you sure you want to reset your filters to default?',
            [new DialogAction('Reset and Start Over', () => { reset(); navigate('/triprate'); updateSessionState(new SessionState()); })],
            '',
            'Cancel'
        );
    };

    const handleViewMap = () => {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set('toTab', 'surveySummary');
        nextParams.set('origin', 'map');
        navigate(
            { pathname: location.pathname, search: nextParams.toString() },
            { replace: true }
        );
        setMapOpen(true);
    };

    const handleCloseMap = () => {
        if (origin === 'map') {
            const nextParams = new URLSearchParams(location.search);
            nextParams.delete('origin');
            navigate(
                { pathname: location.pathname, search: nextParams.toString() },
                { replace: true }
            );
        }

        setMapOpen(false);
    };

    const sites = useMemo(() => {
        return filteredSurveys;
    }, [filteredSurveys, sessionState.selectedSitesAndSurveys]);

    const CountSkeleton = ({ widthFactor = 1 }: { widthFactor?: number }) => (
        <Skeleton
            width={30 * widthFactor}
            height={20}
            sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
        />
    );

    return (
        <>
            <Grid container component={Paper} alignItems="flex-start" sx={{ borderRadius: '0px' }}>
                <Grid size={12}>
                    <Stepper
                        activeStep={currentStepIndex}
                        sx={{
                            '& .MuiStepLabel-label': { fontSize: 12, lineHeight: 1.1 },
                            '& .MuiStepConnector-line': { minHeight: 4 },
                        }}
                    >
                        {steps.map((step, i) => (
                            <Step key={step.label} completed={i < currentStepIndex} disabled={i > currentStepIndex}>
                                <StepLabel
                                    onClick={() => { if (i <= currentStepIndex) goToStep(i); }}
                                    sx={{ cursor: i <= currentStepIndex ? 'pointer' : 'default', userSelect: 'none' }}
                                >
                                    {step.label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Grid>

                <Grid size={{ xs: 6, lg: 4 }} component={Paper} container sx={{ backgroundColor: theme.palette.secondary.main, color: 'white' }}>
                    <Grid size={12} container>
                        <Grid><Typography>Selected Land Use:</Typography></Grid>
                        <Grid size={{ xs: 'grow' }} />
                        <Grid><Typography>{sessionState.tripRateMain}/{sessionState.tripRateSub}</Typography></Grid>
                    </Grid>
                    <Grid size={12} container>
                        <Grid><Typography>Selected Survey Count (ATC):</Typography></Grid>
                        <Grid size={{ xs: 'grow' }} />
                        <Grid>{loading ? <CountSkeleton widthFactor={2} /> : <Typography>{manualCount}({atcCount > 0 ? `${atcCount}` : "0"})</Typography>}</Grid>
                    </Grid>
                    <Grid size={12} container>
                        <Grid><Typography>Surveys Removed due to Re-Survey:</Typography></Grid>
                        <Grid size={{ xs: 'grow' }} />
                        <Grid>{loading ? <CountSkeleton /> : <Typography>{reSurveyCount}</Typography>}</Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 6, lg: 8 }} container>
                    <Grid size={4}>
                        <Button
                            onClick={handleReset}
                            variant="outlined">Start Again</Button>
                    </Grid>
                    <Grid size={4}>
                        <Button
                            onClick={handleViewMap}
                            variant="outlined"
                            disabled={loading || !filteredSurveys.length}
                        >
                            View Map
                        </Button>
                    </Grid>
                    <Grid size={4}>
                        <Button
                            onClick={() => setSummaryOpen(true)}
                            variant="outlined"
                            disabled={loading || !filteredSurveys.length}
                        >
                            View Summary
                        </Button>
                    </Grid>
                    <Grid size={6}>
                        <Button
                            onClick={onBack}
                            disabled={
                                (!isFilterRoute && currentStepIndex === 0) ||
                                ['16', '17', '10'].includes(sessionState.tripRateMain)
                            }
                        >
                            Back
                        </Button>
                    </Grid>
                    <Grid size={6}>
                        {currentStepIndex !== steps.length - 1 && currentStepIndex !== 4 && (
                            <Button
                                onClick={onNext}
                                disabled={
                                    loading ||
                                    ['16', '17', '10'].includes(sessionState.tripRateMain) ||
                                    sessionState.tripRateParamDisabled
                                }
                            >
                                Next
                            </Button>
                        )}
                        {currentStepIndex === 4 && (
                            <Button
                                onClick={handleCalculateTripRate}
                                disabled={loading || ['16', '17', '10'].includes(sessionState.tripRateMain) || sessionState.tripRateParamDisabled}
                            >
                                Calculate Trip Rate
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {sites.length !== 0 && !loading && (
                <SiteMapDialog
                    open={mapOpen}
                    onClose={handleCloseMap}
                    sites={sites}
                    returnOrigin="map"
                    returnTo={location.pathname === '/triprate/summary' ? '/triprate/summary?toTab=surveySummary&origin=map' : undefined}
                />
            )}
            {sites.length !== 0 && !loading && (
                <SummaryDialog
                    open={summaryOpen}
                    handleClose={() => setSummaryOpen(false)}
                />
            )}
        </>
    );
}
